class DOMManager {
  constructor(options = {}) {
    this.editorRef = null

    // Bind methods
    this.initialize = this.initialize.bind(this)
    this.ensureParagraphStructure = this.ensureParagraphStructure.bind(this)
    this.sanitizeHTML = this.sanitizeHTML.bind(this)
    this.handlePaste = this.handlePaste.bind(this)
    this.createNewParagraph = this.createNewParagraph.bind(this)
    this.insertLineBreak = this.insertLineBreak.bind(this)
    this.splitParagraphAtCursor = this.splitParagraphAtCursor.bind(this)
    this.findClosestBlockElement = this.findClosestBlockElement.bind(this)
  }

  initialize({ editorRef }) {
    this.editorRef = editorRef

    // Set up paste handling
    if (this.editorRef?.current) {
      this.editorRef.current.addEventListener("paste", this.handlePaste)
    }
  }

  ensureParagraphStructure() {
    const editor = this.editorRef?.current
    if (!editor) return

    // Check if there are text nodes directly in the editor
    let hasDirectText = false
    for (let node of editor.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        hasDirectText = true
        break
      }
    }

    // If there's direct text or no paragraphs, wrap content in a paragraph
    if (hasDirectText || !editor.querySelector("p, div, h1, h2, h3, h4, h5, h6")) {
      const content = editor.innerHTML.trim()
      if (
        content &&
        !content.startsWith("<p") &&
        !content.startsWith("<div") &&
        !content.startsWith("<h")
      ) {
        editor.innerHTML = `<p>${content}</p>`

        // Restore cursor position at the end of the paragraph
        const p = editor.querySelector("p")
        if (p) {
          const range = document.createRange()
          range.selectNodeContents(p)
          range.collapse(false)
          const selection = window.getSelection()
          selection.removeAllRanges()
          selection.addRange(range)
        }
      }
    }
  }

  sanitizeHTML(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const elements = doc.body.querySelectorAll("*")

    elements.forEach((el) => {
      // Remove style attributes
      el.removeAttribute("style")

      // Get classes and filter only those that start with "ae-"
      const classList = Array.from(el.classList).filter((cls) =>
        cls.startsWith("ae-")
      )

      // Use setAttribute for SVG elements, className for HTML elements
      if (el instanceof SVGElement) {
        el.setAttribute("class", classList.join(" "))
      } else {
        el.className = classList.join(" ")
      }
    })

    return doc.body.innerHTML
  }

  handlePaste(e) {
    e.preventDefault()

    const clipboardData = e.clipboardData || window.clipboardData
    const textData =
      clipboardData.getData("text/html") ||
      clipboardData.getData("text/plain")

    if (textData) {
      // Sanitize pasted content
      const cleanedHtml = this.sanitizeHTML(textData)

      // Insert cleaned content at cursor position
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        range.deleteContents()

        const tempDiv = document.createElement("div")
        tempDiv.innerHTML = cleanedHtml

        while (tempDiv.firstChild) {
          range.insertNode(tempDiv.firstChild)
        }

        // Collapse selection to end
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }

    // Return true to indicate paste was handled
    return true
  }

  findClosestBlockElement(element) {
    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentElement
    }

    return element.closest("p, div, h1, h2, h3, h4, h5, h6") || element
  }

  insertLineBreak() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return false

    const range = selection.getRangeAt(0)
    const br = document.createElement("br")

    // Get the current element
    let currentElement = range.commonAncestorContainer
    const blockElement = this.findClosestBlockElement(currentElement)

    if (blockElement) {
      // Check if cursor is at the beginning of the paragraph
      const isAtBeginning =
        range.startOffset === 0 &&
        (range.startContainer === blockElement ||
          (range.startContainer.nodeType === Node.TEXT_NODE &&
            range.startContainer.parentNode === blockElement))

      if (isAtBeginning) {
        // If at beginning, insert <br> and keep cursor after it
        blockElement.insertBefore(br, blockElement.firstChild)

        // Set cursor after the <br>
        const newRange = document.createRange()
        newRange.setStartAfter(br)
        newRange.setEndAfter(br)
        selection.removeAllRanges()
        selection.addRange(newRange)
      } else {
        // For middle/end positions, insert <br> at cursor position
        range.deleteContents()
        range.insertNode(br)

        // Set cursor after the <br>
        const newRange = document.createRange()
        newRange.setStartAfter(br)
        newRange.setEndAfter(br)
        selection.removeAllRanges()
        selection.addRange(newRange)
      }

      return true
    }

    return false
  }

  createNewParagraph() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return false

    const range = selection.getRangeAt(0)
    const editor = this.editorRef?.current
    if (!editor) return false

    // Check if editor is completely empty
    const isEditorEmpty = !editor.textContent.trim() && !editor.querySelector("p, div, h1, h2, h3, h4, h5, h6")

    if (isEditorEmpty) {
      // Initialize editor with two paragraphs
      const firstParagraph = document.createElement("p")
      firstParagraph.innerHTML = "<br>"
      const secondParagraph = document.createElement("p")
      secondParagraph.innerHTML = "<br>"

      editor.innerHTML = ""
      editor.appendChild(firstParagraph)
      editor.appendChild(secondParagraph)

      // Set cursor in the second paragraph
      const newRange = document.createRange()
      newRange.setStart(secondParagraph, 0)
      newRange.setEnd(secondParagraph, 0)
      selection.removeAllRanges()
      selection.addRange(newRange)

      return true
    }

    // Create a new paragraph element
    const newParagraph = document.createElement("p")
    newParagraph.innerHTML = "<br>"

    // Get the current element
    let currentElement = range.commonAncestorContainer
    const blockElement = this.findClosestBlockElement(currentElement)

    if (blockElement && blockElement !== editor) {
      // Check if current paragraph is empty
      const isEmptyParagraph =
        blockElement.tagName === "P" &&
        (blockElement.innerHTML === "<br>" ||
          blockElement.innerHTML === "" ||
          blockElement.textContent.trim() === "")

      // Check if cursor is at the beginning
      const isAtBeginning =
        range.startOffset === 0 &&
        (range.startContainer === blockElement ||
          (range.startContainer.nodeType === Node.TEXT_NODE &&
            range.startContainer.parentNode === blockElement))

      if (isEmptyParagraph) {
        // For empty paragraphs, just insert new paragraph after
        blockElement.parentNode.insertBefore(
          newParagraph,
          blockElement.nextSibling
        )
      } else if (isAtBeginning) {
        // If cursor is at beginning, insert new paragraph before current one
        blockElement.parentNode.insertBefore(newParagraph, blockElement)

        // Keep cursor in the original paragraph
        const stayRange = document.createRange()
        stayRange.setStart(blockElement, 0)
        stayRange.setEnd(blockElement, 0)
        selection.removeAllRanges()
        selection.addRange(stayRange)
        return true // Exit early to avoid setting cursor in new paragraph
      } else {
        // For paragraphs with content, handle splitting
        this.splitParagraphAtCursor(blockElement, range, newParagraph)
      }
    } else {
      // If we're directly in the editor, wrap existing content and add new paragraph
      const existingContent = editor.innerHTML.trim()
      if (existingContent) {
        const firstParagraph = document.createElement("p")
        firstParagraph.innerHTML = existingContent || "<br>"
        editor.innerHTML = ""
        editor.appendChild(firstParagraph)
        editor.appendChild(newParagraph)
      } else {
        editor.appendChild(newParagraph)
      }
    }

    // Set cursor in the new paragraph
    const newRange = document.createRange()
    newRange.setStart(newParagraph, 0)
    newRange.setEnd(newParagraph, 0)
    selection.removeAllRanges()
    selection.addRange(newRange)

    return true
  }

  splitParagraphAtCursor(blockElement, range, newParagraph) {
    // Extract content after cursor position
    const afterRange = range.cloneRange()
    afterRange.selectNodeContents(blockElement)
    afterRange.setStart(range.startContainer, range.startOffset)

    const afterContent = afterRange.extractContents()

    // Insert new paragraph after current one
    blockElement.parentNode.insertBefore(
      newParagraph,
      blockElement.nextSibling
    )

    // If there's content after the cursor, move it to the new paragraph
    if (
      afterContent.textContent.trim() ||
      afterContent.querySelector("img, figure, button, br")
    ) {
      newParagraph.innerHTML = ""
      newParagraph.appendChild(afterContent)
    }
  }

  cleanUIElements(element) {
    if (!element) return

    // Remove editor UI elements that should not be saved
    const uiElements = element.querySelectorAll(
      '.ae-button-transform-gizmo, .ae-image-transform-gizmo, .ae-transform-gizmo, [class*="transform-gizmo"], [class*="editor-ui"], [data-editor-ui]'
    )
    uiElements.forEach((el) => el.remove())
  }

  normalizeWhitespace(content) {
    if (!content) return ""

    // Remove excessive empty lines
    return content
      .replace(/\n{3,}/g, '\n\n')
      .replace(/(<br\s*\/?>[\s\n]*){3,}/gi, '<br><br>')
      .replace(/(\n[\s]*<br\s*\/?>[\s\n]*){2,}/gi, '\n<br>')
      .replace(/[ \t]+$/gm, '')
  }

  destroy() {
    // Clean up event listeners
    if (this.editorRef?.current) {
      this.editorRef.current.removeEventListener("paste", this.handlePaste)
    }
  }
}

// Export as a plugin-compatible object
export default {
  create: (options) => new DOMManager(options),
  DOMManager,
}